<template>
  <div class="pay">
    <!--=======Selected========-->
    <div class="pay-cur">
      <div :key="tempPay.id" class="pay-cur-row" @click="show = true">
        <b class="row-type">{{ $t('checkout.choose_payment') }}</b>
        <div class="row-info">
          <div class="row-info-base">
            <img :src="curPayIcon" />
            <b class="row-name">{{
              tempPay.displayNameForBuyer || $t('checkout.select')
            }}</b>
            <i class="iconfont dm-right" />
          </div>
          <p v-if="!tempPay.id" class="row-info-tip">
            {{ $t('checkout.choosePaymentTip') }}
          </p>
        </div>
      </div>
      <!--PromoTip-->
      <div v-if="tempPay.promoTips" class="promo text-right">
        <span class="l">{{ calcPromo(tempPay.promoTips)[0] }} </span>
        <span class="r">{{ calcPromo(tempPay.promoTips)[1] }}</span>
      </div>
      <!--WarnTip-->
      <p v-if="payLock" class="pay-cur-warn">
        <q-icon class="icon-warn" name="svg:dm_warn" />
        {{ $t('checkout.virtualWarnTips') }}
      </p>
      <PhoneValid
        v-if="isOVO(tempPay.id)"
        v-model="validForm.phone"
        @done="change(tempPay.id, true, tempPay)"
      />
    </div>
    <!--=======Choose Dialog=======-->
    <q-dialog
      v-model="show"
      content-class="pay-choose-dailog"
      position="bottom"
    >
      <div class="pay-choose">
        <!--Header-->
        <header class="pay-choose-head">
          <span class="head-tile">
            {{ $t('checkout.paymentDialogTitle') }}
          </span>
          <span class="head-icon" @click="show = false">
            <q-icon name="svg:oclose" />
          </span>
        </header>
        <!--E-Wallet-->
        <p v-if="walletPay.length" class="pay-choose-tile notranslate">
          {{ $t('checkout.ewallet') }}
        </p>
        <div v-if="walletPay.length" class="pay-choose-list notranslate">
          <div
            v-for="p of walletPay"
            :key="p.id"
            class="list-item"
            @click="change(p.id, true, p)"
          >
            <div class="list-item-row">
              <img class="payIcon" :src="p.iconUrl" />&nbsp;&nbsp;{{
                p.displayNameForBuyer
              }}
              <div :class="['check', { checked: tempPay.id === p.id }]" />
            </div>
            <div v-if="p.promoTips" class="promo">
              <span class="l">{{ calcPromo(p.promoTips)[0] }} </span>
              <span class="r">{{ calcPromo(p.promoTips)[1] }}</span>
            </div>
          </div>
        </div>
        <!--Virtual Account-->
        <div v-if="virtualPay.length" class="pay-choose-tile">
          {{ $t('checkout.virtualAccount') }}
          <p v-if="!virtualAvariable" class="pay-choose-warn">
            <q-icon class="icon-warn" name="svg:dm_warn" />
            {{ $t('checkout.virtualWarnTips') }}
          </p>
        </div>
        <div
          v-if="virtualPay.length"
          :class="[
            'pay-choose-list notranslate',
            { disable: !virtualAvariable },
          ]"
        >
          <div
            v-for="p of virtualPay"
            :key="p.id"
            class="list-item"
            @click="change(p.id, true, p)"
          >
            <div class="list-item-row">
              <img :src="p.iconUrl" />&nbsp;&nbsp;{{ p.displayNameForBuyer }}
              <div :class="['check', { checked: tempPay.id === p.id }]" />
            </div>
            <div v-if="p.promoTips" class="promo">
              <span class="l">{{ calcPromo(p.promoTips)[0] }} </span>
              <span class="r">{{ calcPromo(p.promoTips)[1] }}</span>
            </div>
          </div>
        </div>
        <!--Cash-->
        <div v-if="offlinePay.length" class="pay-choose-list">
          <div
            v-for="p of offlinePay"
            :key="p.id"
            class="list-item cash"
            @click="change(p.id, true, p)"
          >
            <div class="list-item-row">
              <img :src="p.iconUrl" />
              <div class="list-item-text" v-text="p.text" />
              <div :class="['check', { checked: tempPay.id === p.id }]" />
            </div>
            <div class="list-item-tip" v-html="p.tip" />
            <div v-if="p.promoTips" class="promo">
              <span class="l">{{ calcPromo(p.promoTips)[0] }} </span>
              <span class="r">{{ calcPromo(p.promoTips)[1] }}</span>
            </div>
          </div>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script setup>
import {
  watch,
  inject,
  defineProps,
  ref,
  computed,
  defineEmits,
  defineExpose,
  nextTick,
  onMounted,
} from 'vue';
import { use18n, useIns } from 'src/utils/v2_helper/common';
import { isOVO, calcPromo } from '../../utils';
import PhoneValid from './PhoneValid.vue';
import { isQRIS } from './config';

const PAY_TYPE = {
  EWALLET: 'e-wallet',
  BANK_TRANS: 'bank-transfer',
  OFFLINE: 'off-line',
};
const app = useIns();
const { $t } = use18n();
const payment = inject('payment');
const vouchers = inject('vouchers');

const emits = defineEmits(['onLockPay', 'changePayment', 'update:payLock']);
const props = defineProps({
  payLock: { type: Boolean, default: false },
  paymentMethods: { type: Array, default: () => [] },
});
const show = ref(false);
const tempPay = ref({});
const validForm = ref({
  phone: '',
});

const walletPay = computed(() => {
  const payments = props.paymentMethods
    .filter((p) => p.type === PAY_TYPE.EWALLET)
    .map((it) => ({
      ...it,
      valid: isOVO(it.id),
    }));
  const promoIndex = payments.findIndex((it) => Boolean(it.promoTips));
  if (promoIndex !== -1) {
    [payments[promoIndex], payments[0]] = [payments[0], payments[promoIndex]];
  }
  return payments;
});
const virtualPay = computed(() =>
  props.paymentMethods.filter((p) => p.type === PAY_TYPE.BANK_TRANS),
);
const offlinePay = computed(() => {
  const ps = props.paymentMethods
    .filter((p) => p.type === PAY_TYPE.OFFLINE)
    .map((it) => {
      if (it.id === 'CASH') {
        return {
          ...it,
          tip: $t('checkout.payCashTip'),
          text: $t('checkout.payByCard'),
        };
      }
      return it;
    });
  return ps;
});
const payType = computed(() => {
  const m = {
    [PAY_TYPE.EWALLET]: $t('checkout.ewallet'),
    [PAY_TYPE.BANK_TRANS]: $t('checkout.virtualAccount'),
    [PAY_TYPE.OFFLINE]: $t('checkout.cashPayment'),
  };
  return m[tempPay.value.type] || $t('checkout.choosePayment');
});
const virtualAvariable = computed(() => {
  if (virtualPay.value.length === 0) return true;
  return virtualPay.value[0] && virtualPay.value[0].available;
});
const isVirtual = computed(() => tempPay.value.type === PAY_TYPE.BANK_TRANS);
const curPayIcon = computed(
  () =>
    tempPay.value.iconUrl ||
    'https://static.bakmigm.co.id/menu/paymentIcon/select_pay.svg',
);

function change(payId, manual, pay) {
  const existPay = pay || props.paymentMethods.find((it) => it.id === payId);
  emits('onLockPay', false);
  emits('changePayment', {
    manual,
    payId,
    phoneNumber: isOVO(existPay.id)
      ? `+62${validForm.value.phone.substr(1)}`
      : '',
    ...pay,
  });
  tempPay.value = existPay;
  show.value = false;
  localStorage.setItem('TEMP_PAYMENT_ID', payId);
}

function withExistPayment(defaultPay) {
  const isSupportExist = props.paymentMethods.find(
    (it) => it.name === payment.value?.name,
  );
  if (payment.value?.payId && isSupportExist) return payment.value;
  return defaultPay;
}
// 1. 唯一开启(可用)  2. 优惠  3. 上次支付 4. 开启了qris
function getDefaultPay() {
  // 如果可用支付方式唯一优先级判断，那直接默认选中
  const avaPays = props.paymentMethods.filter((it) => it.available);
  if (avaPays.length === 1) return withExistPayment(avaPays[0]);
  // 优惠券优先级判断
  const promoPay = props.paymentMethods.find(
    (it) => it.promoTips && !it.ignoreSetDefualtPay,
  );
  console.log('DEF_PAY: ', promoPay);
  if (promoPay) return withExistPayment(promoPay);
  // 上次支付方式优先级判断
  const defaultPay = props.paymentMethods.find((it) => it.default);
  console.log('DEF_PAY: ', defaultPay);
  if (defaultPay) return withExistPayment(defaultPay);
  // QRIS 判断
  const qrisPayment = props.paymentMethods.find((it) => isQRIS(it));
  if (qrisPayment) return withExistPayment(qrisPayment);
  return payment.value;
}

defineExpose({ isVirtual, change });

watch(
  [virtualAvariable, payment],
  ([vir, p], _) => {
    nextTick(() => {
      const isVir = p.type === PAY_TYPE.BANK_TRANS;
      // 使用voucher后价格不支持虚拟支付，禁止支付
      emits('update:payLock', isVir && !vir);
    });
  },
  { immediate: true, deep: true },
);

onMounted(async () => {
  const phone = app?.userInfo.phone ?? '';
  validForm.value.phone = phone.startsWith('+62')
    ? `0${phone?.substring(3)}`
    : '';
  await nextTick();
  const defaultPay = getDefaultPay();
  if (defaultPay) {
    change(defaultPay.id, true, defaultPay);
  }
});
</script>

<style lang="scss">
.pay-choose-dailog {
  .q-dialog__inner {
    padding: 0;
    .pay-choose {
      max-width: 100%;
    }
  }
}
.pay,
.pay-choose {
  .promo {
    // margin-bottom: 8px;
    span {
      color: $primary !important;
    }
    font-size: 12px;
    .l {
      font-weight: bold;
    }
  }
}
</style>
<style lang="scss" scoped>
p {
  margin: 0;
}
.text-right {
  text-align: right;
}
.icon-warn {
  fill: $negative;
  width: 16px;
  height: 16px;
  margin-right: 4px;
}
.pay {
  &-tile {
    font-weight: bold;
    font-size: 12px;
    padding-bottom: 8px;
  }
  &-cur {
    margin-bottom: 24px;
    &-row {
      line-height: 40px;
      display: flex;
      .row-info {
        &-base {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        &-tip {
          font-size: 12px;
          color: $negative;
        }
      }
      .row-type {
        margin-right: auto;
      }
      img {
        width: 20px;
        margin-right: 8px;
      }
      .dm-right {
        scale: 1.4;
      }
    }
    span {
      color: #374151;
    }
    &-icon {
      width: 20px;
      height: 20px;
      padding: 8px 0 8px 10px;
    }
    &-warn {
      color: $negative;
      display: flex;
      align-items: center;
    }
  }
  &-choose {
    width: 100%;
    height: 100%;
    background: #fff;
    padding: 16px;
    padding-top: 60px;
    &-head {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: bold;
      color: #111827;
      padding: 0 16px;
      background: #fff;
      z-index: 999;
    }
    &-tile {
      font-weight: bold;
      font-size: 12px;
    }
    &-warn {
      font-size: 12px;
      color: $negative;
      font-weight: normal;
      display: flex;
      margin-top: 4px;
    }
    &-list {
      padding-bottom: 26px;
      &.disable {
        opacity: 0.4;
        pointer-events: none;
      }
      .list-item {
        padding: 16px 0;
        border-bottom: solid 1px #d1d5db;
        &-row {
          display: flex;
          font-weight: bold;
          align-items: center;
          .check {
            margin-left: auto;
            border: 2px solid $dark;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            position: relative;
            &.checked::before {
              content: '';
              width: 10px;
              height: 10px;
              background-color: $dark;
              position: absolute;
              left: 50%;
              top: 50%;
              border-radius: 50%;
              transform: translate(-50%, -50%);
            }
          }
          img {
            width: 24px;
            height: 24px;
          }
        }
        &.cash {
          display: block;
          img {
            width: 20px;
          }
        }
        &-text {
          margin-left: 6px;
        }
        &-tip {
          color: #6b7280;
          font-size: 12px;
          margin-top: 10px;
        }
      }
    }
  }
}
</style>
